import type { FC } from "react";
import { useMemo, useState } from "react";

import { styled } from "styled-components";

import type { ITableProps } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";

import { useTranslation } from "pro-features/components/WithTranslation";

import { useMonitorAndProtectAdKeywords } from "../../../services/api/ads";
import { Widget } from "../../../shared/ui/layout/widget";
import { getNoData } from "../../../shared/ui/table/get-no-data";
import type { IExampleTableRow } from "../table-config";

import { adKeywordsColumnBuilder, adKeywordsRowBuilder } from "./ad-keywords-table-config";
import type { IAdKeywordsTableRowWithEmpty } from "./ad-keywords-table-data.types";

const Container = styled(Widget)<{ withBottomPadding?: boolean }>`
    ${({ withBottomPadding }) => withBottomPadding && `padding-bottom: 12px;`}
`;

interface IAdKeywordsTableProps {
    adItem?: IExampleTableRow;
    onClose?: () => void;
}

export const AdKeywordsTable: FC<IAdKeywordsTableProps> = ({ adItem, onClose }) => {
    const translate = useTranslation();
    const [activeSorting, setActiveSorting] = useState<{ field: string; order: "ascend" | "descend" }>({
        field: "volume",
        order: "descend",
    });

    const { data, isError, isLoading } = useMonitorAndProtectAdKeywords(adItem.id);

    const columnConfig = useMemo(() => {
        return adKeywordsColumnBuilder.build().map((item) => {
            item.title = translate(item.title as string);
            item.tooltipTitle = translate(item.tooltipTitle);
            item.tooltipSubtitle = translate(item.tooltipSubtitle);
            return item;
        });
    }, [translate]);

    const columnConfigWithSorting = useMemo(() => {
        return columnConfig.map((item) => {
            if (item.dataIndex === activeSorting.field) {
                return {
                    ...item,
                    sortOrder: activeSorting.order,
                };
            }

            return item;
        });
    }, [activeSorting.field, activeSorting.order, columnConfig]);

    const trackSorting: ITableProps<IAdKeywordsTableRowWithEmpty>["onSort"] = (sorter) => {
        setActiveSorting({
            field: sorter.field as string,
            order: sorter.order,
        });
    };

    return (
        <Container
            withBottomPadding={data?.records.length > 10}
            data-automation="ad-keywords-table-container"
        >
            <Widget.Header>
                {translate("monitor_and_protect.page.ads.keywords_table.header")}
                <IconButton
                    type="flat"
                    iconName="clear"
                    iconSize="16px"
                    onClick={onClose}
                />
            </Widget.Header>
            <Table
                isLoading={isLoading}
                pagination={{
                    pageSize: 10,
                    total: data?.records.length || 0,
                }}
                noData={getNoData(isError, "keywords", translate)}
                onSort={trackSorting}
                columnConfig={columnConfigWithSorting}
                data={data?.records ? adKeywordsRowBuilder.transformData(data?.records) : [undefined]}
            />
        </Container>
    );
};
